import { forwardRef, useId } from 'react'

import type { IconButtonProps } from '@enechain/ecloud-designsystem'
import {
  IconButton,
  Tooltip,
  VisuallyHidden,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'

type TooltipControl = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type IconTooltipProps = {
  content: React.ReactNode
  showArrow?: boolean
  /**
   * 子のボタンの `disabled`。 渡された時 (`false` を含む) は `ButtonTooltip` の span で包み、
   * 押せない間も hover と focus を拾う。 渡されない時は span を挟まず DS `Tooltip` を直接付ける。
   */
  disabled: boolean | undefined
  /** span で包まない時 (`disabled` を渡さない時) だけ効く。 [[IconOnlyButton]] の型が排他にしている。 */
  tooltipControl?: TooltipControl
  /** 子のボタン。 DS `Tooltip` が trigger にするので ref と残りの props を転送すること。 */
  children: React.ReactElement
}

/**
 * span を挟むかどうかは `disabled` を**渡したか**で決め、 値では決めない。 値で切り替えると
 * disabled になった瞬間に間の要素が増え、 中のボタンが作り直されて focus が外れる
 * (`ButtonTooltip` の JSDoc「説明が無い間も Tooltip は置いたまま閉じておく」と同じ理由)。
 */
const IconTooltip = ({
  content,
  showArrow,
  disabled,
  tooltipControl,
  children,
}: IconTooltipProps): React.ReactElement => {
  if (disabled !== undefined) {
    return (
      <ButtonTooltip content={content} showArrow={showArrow}>
        {children}
      </ButtonTooltip>
    )
  }
  const control =
    tooltipControl === undefined
      ? {}
      : {
          open: tooltipControl.open,
          // app の moduleResolution (node10) では chakra が参照する `@ark-ui/react/*` の
          // 型が解決できず details が any になるため、 使う field だけ型を書く。
          onOpenChange: (details: { open: boolean }) => {
            tooltipControl.onOpenChange(details.open)
          },
        }
  return (
    <Tooltip content={content} showArrow={showArrow} {...control}>
      {children}
    </Tooltip>
  )
}

/**
 * icon-only のボタンに、 名前 (`label`) と同じ文言の DS `Tooltip` を付ける。
 *
 * アイコンだけでは何のボタンか見て分からないので、 読み上げ名と同じ文言を hover / focus で出す
 * (native `title` は出るまで遅く見た目も OS 依存)。 disabled のボタンは pointer event を
 * 受けず Tooltip を取りこぼすので、 Tooltip を受ける span で包む。 見た目の違う画面固有の
 * icon-only ボタン (AdminWorkflowTemplates の `StepIconButton`) が使う。
 */
export const IconOnlyLabelTooltip = ({
  label,
  disabled,
  children,
}: {
  /** ボタンの名前。 DS `Tooltip` の文言にもなる。 */
  label: string
  disabled: boolean | undefined
  children: React.ReactElement
}): React.ReactElement => (
  <IconTooltip content={label} disabled={disabled}>
    {children}
  </IconTooltip>
)

/**
 * Tooltip の開閉の制御 (`tooltipControl`) と `disabled` は同時に使えない。 開閉の制御は
 * span で包まない側 (DS `Tooltip` を直接付ける) にしか効かないため、 型で分ける。
 */
type DisabledOrControl =
  | {
      /** [[IconTooltip]] の `disabled` と同じ。 */
      disabled?: boolean
      /**
       * disabled の間、 Tooltip に名前の代わりに出し、 読み上げでも説明 (`aria-describedby`)
       * として伝える押せない理由。
       */
      disabledReason?: string
      tooltipControl?: never
    }
  | {
      disabled?: never
      disabledReason?: never
      /** Tooltip の開閉を外から決める (popover を開いている間は閉じておく等)。 */
      tooltipControl: TooltipControl
    }

type Props = Omit<IconButtonProps, 'aria-label' | 'disabled'> &
  DisabledOrControl & {
    /**
     * ボタンの名前。 `aria-label` と DS `Tooltip` の文言になる。
     * `asChild` で link にする時も、 名前はここで渡す (DS `IconButton` が子の要素へ移す)。
     */
    label: string
    /**
     * 読み上げ名にだけ前に付ける文脈 (例: 列名)。 `aria-label` は `${labelContext} / ${label}`、
     * Tooltip は `label` だけになる (Tooltip の文言は読み上げ名に含まれる)。
     */
    labelContext?: string
    /**
     * 名前とは別の説明。 渡すと Tooltip は名前の代わりにこれを出し、 同じ内容を
     * `aria-describedby` で読み上げにも渡す。
     */
    description?: React.ReactNode
    /** Tooltip に矢印を付ける。 */
    showArrow?: boolean
  }

const isEmpty = (node: React.ReactNode): boolean =>
  node === undefined || node === null || node === ''

/**
 * アイコンだけのボタン。 DS `IconButton` の唯一の入口。
 *
 * Tooltip に出す内容は、 どの props でも読み上げで得られる: 名前は `aria-label`、
 * `description` と `disabledReason` は visually-hidden の要素を指す `aria-describedby`。
 * DS `IconButton` を直接使うとこの対応・Tooltip の付け忘れ・disabled 時の span 包みが
 * 漏れるので、 直接の import は lint (`@ebalance/eslint-config` の restricted-imports) が
 * 止めていて、 この file だけを除外している。
 */
export const IconOnlyButton = forwardRef<HTMLButtonElement, Props>(
  function IconOnlyButton(
    {
      label,
      labelContext,
      description,
      showArrow,
      disabled,
      disabledReason,
      tooltipControl,
      ...rest
    },
    ref,
  ) {
    const descriptionId = useId()
    const reason =
      disabled === true && disabledReason !== undefined
        ? disabledReason
        : undefined
    const described = reason ?? description
    const hasDescription = !isEmpty(described)
    return (
      <>
        <IconTooltip
          content={reason ?? description ?? label}
          disabled={disabled}
          showArrow={showArrow}
          tooltipControl={tooltipControl}
        >
          <IconButton
            ref={ref}
            {...rest}
            {...(hasDescription ? { 'aria-describedby': descriptionId } : {})}
            aria-label={
              labelContext === undefined ? label : `${labelContext} / ${label}`
            }
            disabled={disabled}
          />
        </IconTooltip>
        {hasDescription && (
          <VisuallyHidden id={descriptionId}>{described}</VisuallyHidden>
        )}
      </>
    )
  },
)
